import * as React from 'react'
import styled from '@theme/styled-components'
import { View, Text, TouchableOpacity } from 'react-native'

import TextInputComponent from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import KeyboardView from '@components/view/KeyboardView'

import logger from '@utils/logger'
import { useFormik } from 'formik'
import * as yup from 'yup'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import api from '@api/api'

interface Props {
  onBack: () => void
}

interface Values {
  email: string
}

const initialValues = {
  email: '',
}

const validationSchema = yup.object().shape({
  email: yup.string().email('errors.form.email').required('errors.form.empty'),
})

const ForgetPassword = ({ onBack }: Props) => {
  const i18n = useI18n()
  const [status, setStatus] = React.useState<Status>()
  const [isEmailKnown, setIsEmailKnown] = React.useState(false)
  const [, bp] = useBreakpoint()

  const onSubmitForm = (values: Values) => {
    setStatus('loading')
    api.authentification
      .forgottenPassword(values.email)
      .then(() => setStatus('fetched'))
      .catch((err) => {
        logger.error(err)
        setStatus('error')
      })
  }

  const { values, touched, handleChange, handleBlur, errors, handleSubmit, isValid } = useFormik<Values>({
    initialValues,
    onSubmit: onSubmitForm,
    validationSchema,
  })

  React.useEffect(() => {
    if (!!values.email && !errors.email) {
      api.authentification.isEmailKnown(values.email).then(setIsEmailKnown).catch(logger.error)
    }
  }, [values.email, errors])

  return (
    <MainContainer>
      <KeyboardView>
        {!status || status === 'loading' ? (
          <>
            {bp !== 'phone' && (
              <BackButton onPress={onBack}>
                <Icon name="long-arrow-left" size={40} color="black" />
              </BackButton>
            )}
            <Title>{i18n.t('screens.login.forgetPassword.title')}</Title>
            <View>
              <BaseLine>{i18n.t('screens.login.forgetPassword.text')}</BaseLine>
              <TextInputComponent
                placeholder={i18n.t('screens.login.forgetPassword.placeholder')}
                onChange={handleChange('email')}
                value={values.email}
                right={{ name: 'mail' }}
                onBlur={handleBlur('email')}
                error={
                  touched.email
                    ? errors.email
                      ? i18n.t(errors.email)
                      : !isEmailKnown
                      ? i18n.t('screens.login.forgetPassword.unknownEmail')
                      : undefined
                    : undefined
                }
              />
              <ButtonContainer>
                <Button
                  label={i18n.t('screens.login.forgetPassword.button')}
                  onClick={handleSubmit}
                  status={
                    status === 'loading' ? 'loading' : isValid && values.email && isEmailKnown ? 'active' : 'disabled'
                  }
                />
              </ButtonContainer>
            </View>
          </>
        ) : (
          <>
            <Title>{i18n.t(`screens.login.mailSend.${status === 'fetched' ? 'title' : 'errorTitle'}`)}</Title>
            <BaseLine>
              {i18n.t(`screens.login.mailSend.${status === 'fetched' ? 'subTitle' : 'errorSubTitle'}`, {
                email: values.email,
              })}
            </BaseLine>
            <ButtonContainer>
              <Button
                label={i18n.t(status === 'error' ? 'common.cancel' : 'screens.login.mailSend.button')}
                onClick={onBack}
                status="active"
              />
            </ButtonContainer>
          </>
        )}
      </KeyboardView>
    </MainContainer>
  )
}

const MainContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `0px 16px 20px 16px` : '0px 48px')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1; justify-content: center;'}
`
const BackButton = styled(TouchableOpacity)`
  margin-bottom: 30px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 48px;
  line-height: 56px;
  margin-bottom: 16px;
`
const BaseLine = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 28px;
  color: ${(props) => props.theme.colors.secondaryText};
  margin-bottom: 48px;
  line-height: 32px;
`

const ButtonContainer = styled(View)`
  margin-top: 40px;
  margin-bottom: 20px;
  ${(props) => props.theme.breakpoint !== 'phone' && `max-width: 280px;`}
`

export default ForgetPassword
